import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import styled from "styled-components";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { Dropdown } from "semantic-ui-react";
import { GET_EMPLOYEES, SG_EMPLOYEE_CATEGORY_SEARCH } from "constants/actions";
import { useUserSearchContext, UserSearchProvider } from "Providers/UserSearchProvider";
import SearchDropdown from "new_components/organisms/SearchDropdown";

// Import the pulse survey form components that are already being used
import EmailTemplate from "./SurveyFormCategories/EmailTemplate";
import ReportAccessSection from "./SurveyFormCategories/ReportAccess";

// Import the survey context
import { useSurveyContext } from "./Data/SurveyFormContext";

// Icons
const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
  </svg>
)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z" />
  </svg>
)

const EmailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C0,4.89 21.1,4 20,4Z" />
  </svg>
)

const UsersIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
)

const ReportIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
  </svg>
)

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
  </svg>
)

const PulseScheduleSurvey = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Get survey context functions
  const { formData, updateFormData, handleSubmit, saveDraft, loading } =
    useSurveyContext();

  // Get initial survey data from navigation state (from SurveyLibrary or existing draft from PulseBuilder)
  const initialSurveyData = location.state?.initialSurveyData || null;
  const existingDraftData = location.state?.existingDraftData || null;

  // Determine if we're working with an existing draft or creating from template
  const surveyData = existingDraftData || initialSurveyData;
  const isExistingDraft = Boolean(existingDraftData);

  // Step management - extended to support 3 steps (1 = Email Details, 2 = Assign Recipients, 3 = Report Access)
  const [wizardStep, setWizardStep] = useState(1);
  
  // Track if we're sending (not just saving draft) to control navigation
  const [isSending, setIsSending] = useState(false);

  // Email template state (from original QuickSend modal)
  const [emailTpl, setEmailTpl] = useState({
    email_subject:
      surveyData?.emailSubject ||
      formData?.emailSubject ||
      `Survey: ${surveyData?.name || formData?.name || ""}`,
    email_content:
      surveyData?.emailBody ||
      formData?.emailBody ||
      `Hi,\n\nYou have been invited to participate in the survey "${
        surveyData?.name || formData?.name || ""
      }". Please follow the link to complete the survey.\n\nThank you!`,
  });

  // Assignment state - use the original user object format
  const initialAssignedUsers = surveyData?.assignedUsersList || formData?.assignedUsersList || [];
  const [assignedUsersList, setAssignedUsersList] = useState(
    Array.isArray(initialAssignedUsers) ? initialAssignedUsers : []
  );

  // UI state
  const [activeTab, setActiveTab] = useState("search"); // "search" or "category"

  // Bulk category selection state
  const [bulkCategoryId, setBulkCategoryId] = useState(null);
  const [bulkCategoryValueId, setBulkCategoryValueId] = useState(null);

  const dispatch = useDispatch();

  // Redux state for categories
  const {
    get_survey_structure,
    employeeCategorySearch,
    employees,
  } = useSelector(
    (state) => ({
      get_survey_structure: Array.isArray(
        state.surveystructure?.survey_structure
      )
        ? state.surveystructure.survey_structure
        : [],
      employeeCategorySearch: state.employee_category?.search,
      employees: Array.isArray(state.employees?.employees)
        ? state.employees.employees
        : [],
    }),
    shallowEqual
  );

  // Report access collaborators state
  const [reportAccessUsers, setReportAccessUsers] = useState(
    surveyData?.reportAccessUsers || formData?.collaborators || []
  );

  // Survey data - use formData from context if available, otherwise fallback to surveyData
  const [quickSurvey] = useState(formData?.name ? formData : surveyData);

  // Updated navigation handlers for 3-step wizard
  const goToAssign = () => {
    setWizardStep(2);
  };

  const goToReportAccess = () => {
    setWizardStep(3);
  };

  const goBackToEmail = () => {
    setWizardStep(1);
  };

  const goBackToAssign = () => {
    setWizardStep(2);
  };

  // Load employees on mount
  useEffect(() => {
    if (!employees?.length) {
      dispatch({ type: GET_EMPLOYEES });
    }
  }, [dispatch, employees?.length]);

  // Build filterCategories for bulk dropdowns
  const filterCategories = useMemo(() => {
    if (!get_survey_structure?.[0]?.categories) return [];

    return get_survey_structure[0].categories
      .filter((cat) => cat.visibility !== "private" && !cat.demographic)
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
        priority: cat.priority,
        options: (cat.options || []).map((opt) => ({
          id: opt.id,
          name: opt.name,
          text: opt.text || opt.name,
        })),
      }));
  }, [get_survey_structure]);

  // Derive the currently selected category + value
  const selectedBulkCategory = useMemo(
    () => filterCategories.find((cat) => cat.id === bulkCategoryId) || null,
    [filterCategories, bulkCategoryId]
  );

  const selectedBulkCategoryValue = useMemo(
    () =>
      selectedBulkCategory?.options?.find(
        (opt) => opt.id === bulkCategoryValueId
      ) || null,
    [selectedBulkCategory, bulkCategoryValueId]
  );

  // Sorted options for the Value dropdown
  const sortedCategoryValueOptions = useMemo(() => {
    if (!selectedBulkCategory?.options) return [];
    return [...selectedBulkCategory.options]
      .sort((a, b) => {
        const textA = (a.text || a.name || '').toLowerCase();
        const textB = (b.text || b.name || '').toLowerCase();
        return textA.localeCompare(textB);
      })
      .map((opt) => ({
        key: opt.id,
        text: opt.text || opt.name || '',
        value: opt.id,
      }));
  }, [selectedBulkCategory]);

  // Derive bulk search loading + data from employeeCategorySearch
  const bulkLoading = !!employeeCategorySearch?.pending;
  const bulkMatchCount = employeeCategorySearch?.count || 0;
  const bulkResults = employeeCategorySearch?.results || [];

  // Handle adding a user from SearchDropdown
  const handleAddUser = useCallback((user) => {
    if (user.value !== -1 && !assignedUsersList.some((u) => u.value === user.value)) {
      const updatedUsers = [...assignedUsersList, user];
      setAssignedUsersList(updatedUsers);
    }
  }, [assignedUsersList]);

  // Handle removing a user
  const handleRemoveUser = useCallback((userId) => {
    const updatedUsers = assignedUsersList.filter((user) => user.value !== userId);
    setAssignedUsersList(updatedUsers);
  }, [assignedUsersList]);

  // Handle selection from SearchDropdown (basic handler - group handling in component)
  const handleSelectItem = useCallback((item) => {
    if (item.type !== "group") {
      handleAddUser(item);
    }
    // Group handling will be done in AssignRecipientsContent with access to userList
  }, [handleAddUser]);

  // Bulk category handlers - convert employee results to user objects
  const handleBulkCategoryPreview = () => {
    if (!selectedBulkCategory || !selectedBulkCategoryValue) {
      return;
    }
    dispatch({
      type: SG_EMPLOYEE_CATEGORY_SEARCH,
      payload: {
        page: 1,
        page_size: 10000,
        category_name_id: selectedBulkCategory.id,
        category_value_id: selectedBulkCategoryValue.id,
        category_priority: selectedBulkCategory.priority,
        ordering: "employee__first_name",
      },
    });
  };

  const handleBulkCategoryConfirm = () => {
    if (!bulkResults || bulkResults.length === 0) return;

    // Convert employee results to user object format
    const newUsers = bulkResults
      .map((row) => {
        const empId = row.employee_id || row.employee || row.id;
        const emp = employees.find(e => e.id === empId);
        if (emp && !assignedUsersList.some(u => u.value === empId)) {
          return {
            value: emp.id,
            text: `${emp.first_name} ${emp.last_name}`,
            id: emp.id,
            key: emp.id,
          };
        }
        return null;
      })
      .filter(Boolean);

    if (newUsers.length > 0) {
      setAssignedUsersList((prev) => [...prev, ...newUsers]);
    }

    // Clear bulk selection
    setBulkCategoryId(null);
    setBulkCategoryValueId(null);
  };

  // Clear all selected users
  const clearAllUsers = () => {
    setAssignedUsersList([]);
    setBulkCategoryId(null);
    setBulkCategoryValueId(null);
  };

  // Use ref to track previous values and prevent infinite loops
  const prevValuesRef = useRef({
    emailSubject: emailTpl.email_subject,
    emailContent: emailTpl.email_content,
    assignedUsersList: JSON.stringify(assignedUsersList),
    reportAccessUsers: JSON.stringify(reportAccessUsers),
    isExistingDraft,
  });

  // Update form data in context when local state changes
  useEffect(() => {
    if (!surveyData) return;

    const currentValues = {
      emailSubject: emailTpl.email_subject,
      emailContent: emailTpl.email_content,
      assignedUsersList: JSON.stringify(assignedUsersList),
      reportAccessUsers: JSON.stringify(reportAccessUsers),
      isExistingDraft,
    };

    // Only update if values have actually changed
    const hasChanged = Object.keys(currentValues).some(
      key => currentValues[key] !== prevValuesRef.current[key]
    );

    if (hasChanged) {
      prevValuesRef.current = currentValues;
      updateFormData({
        ...surveyData,
        name: surveyData.name || "",
        description: surveyData?.user_tasks?.find(
              (task) => task && task.task_type === "description"
            )?.text || surveyData.draftData?.description || surveyData.description || "",
        questions: surveyData.questions || [],
        emailSubject: emailTpl.email_subject,
        emailBody: emailTpl.email_content,
        assignedUsersList,
        collaborators: reportAccessUsers,
        isExistingDraft,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    emailTpl.email_subject,
    emailTpl.email_content,
    assignedUsersList,
    reportAccessUsers,
    isExistingDraft,
  ]);

  const handleSaveDraft = () => {
    if (quickSurvey) {
      // Update the form data with current wizard state before saving draft
      updateFormData({
        ...quickSurvey,
        name: quickSurvey.name || "",
        description: quickSurvey?.user_tasks?.find(
              (task) => task && task.task_type === "description"
            )?.text || quickSurvey.draftData?.description || quickSurvey.description || "",
        questions: quickSurvey.questions || [],
        emailSubject: emailTpl.email_subject,
        emailBody: emailTpl.email_content,
        assignedUsersList, // This is now computed from selectedEmployees
        collaborators: reportAccessUsers,
        isExistingDraft, // Pass this flag to context
      });

      // Use the context's saveDraft function with isTemplate = false for drafts
      saveDraft(false);
    }
  };

  const sendNow = () => {
    if (quickSurvey) {
      // Set the sending flag to control navigation
      setIsSending(true);
      
      // Update the form data with current wizard state before submitting
      updateFormData({
        ...quickSurvey,
        name: quickSurvey.name || "",
        description: quickSurvey.description || quickSurvey.draftData?.description || "",
        questions: quickSurvey.questions || [],
        emailSubject: emailTpl.email_subject,
        emailBody: emailTpl.email_content,
        assignedUsersList,
        collaborators: reportAccessUsers,
      });

      // Use the context's handleSubmit function to create the survey
      handleSubmit();
    }
  };

  const closeQuickSend = () => {
    // If we just sent a survey (not just saved draft), go to main schedule page
    if (isSending) {
      navigate("/leader/audit/schedule-v2");
      return;
    }

    // Check if we have a returnTo path in location state
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo, { replace: true });
      return;
    }

    // Check if we came from PulseBuilder (existingDraftData indicates this)
    if (existingDraftData) {
      // If we have a survey ID, go to edit mode, otherwise go to pulse main page
      const surveyId = existingDraftData.id || formData?.id;
      if (surveyId) {
        navigate(`/leader/audit/schedule-v2/pulse/edit/${surveyId}`, { 
          state: { returnTo: "/leader/audit/schedule-v2", from: "schedule" }
        });
      } else {
        navigate("/leader/audit/schedule-v2/pulse");
      }
      return;
    }

    // Default fallback - go to library if we have survey data, otherwise schedule hub
    if (initialSurveyData) {
      navigate("/leader/audit/schedule-v2/pulse/library");
    } else {
      navigate("/leader/audit/schedule-v2");
    }
  };

  // Redirect if no survey data
  if (!quickSurvey) {
    navigate("/leader/audit/schedule-v2/pulse/library");
    return null;
  }

  return (
    <Container>
      {/* Header */}
      <HeaderSection>
        <BackButton onClick={closeQuickSend}>
          <ArrowLeftIcon />
          <span>Back</span>
        </BackButton>

        <HeaderContent>
          <IconWrapper>
            <CalendarIcon />
          </IconWrapper>
          <HeaderText>
            <Title>
              {isExistingDraft ? "Send Survey" : "Schedule Survey"}:{" "}
              {quickSurvey.name}
            </Title>
            <Subtitle>
              {wizardStep === 1
                ? "Step 1: Configure email template"
                : wizardStep === 2
                ? "Step 2: Select survey recipients"
                : "Step 3: Configure report access (optional)"}
            </Subtitle>
          </HeaderText>
        </HeaderContent>

        {/* Progress Indicator */}
        <ProgressIndicator>
          <ProgressStep active={wizardStep === 1} completed={wizardStep > 1}>
            <ProgressStepNumber
              active={wizardStep === 1}
              completed={wizardStep > 1}
            >
              1
            </ProgressStepNumber>
            <ProgressStepText>Email Details</ProgressStepText>
          </ProgressStep>
          <ProgressConnector completed={wizardStep > 1} />
          <ProgressStep active={wizardStep === 2} completed={wizardStep > 2}>
            <ProgressStepNumber
              active={wizardStep === 2}
              completed={wizardStep > 2}
            >
              2
            </ProgressStepNumber>
            <ProgressStepText>Assign Recipients</ProgressStepText>
          </ProgressStep>
          <ProgressConnector completed={wizardStep > 2} />
          <ProgressStep active={wizardStep === 3}>
            <ProgressStepNumber active={wizardStep === 3}>3</ProgressStepNumber>
            <ProgressStepText>Report Access</ProgressStepText>
          </ProgressStep>
        </ProgressIndicator>
      </HeaderSection>

      {/* Step Content */}
      <FormContainer>
        <StepFormLayout>
          <StepContent>
            <PanelSection>
              <SectionTitle>
                {wizardStep === 1 ? (
                  <>
                    <EmailIcon />
                    <span>Email Template</span>
                  </>
                ) : wizardStep === 2 ? (
                  <>
                    <UsersIcon />
                    <span>
                      Select Recipients ({assignedUsersList.length} selected)
                    </span>
                  </>
                ) : (
                  <>
                    <ReportIcon />
                    <span>
                      Report Access ({reportAccessUsers.length} users with
                      access)
                    </span>
                  </>
                )}
              </SectionTitle>

              {wizardStep === 1 && (
                <EmailTemplate
                  emailTemplate={emailTpl}
                  onUpdate={setEmailTpl}
                  errors={{}}
                />
              )}

              {wizardStep === 2 && (
                <UserSearchProvider>
                  <AssignRecipientsContent
                    assignedUsersList={assignedUsersList}
                    setAssignedUsersList={setAssignedUsersList}
                    handleSelectItem={handleSelectItem}
                    handleRemoveUser={handleRemoveUser}
                    clearAllUsers={clearAllUsers}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    filterCategories={filterCategories}
                    bulkCategoryId={bulkCategoryId}
                    setBulkCategoryId={setBulkCategoryId}
                    bulkCategoryValueId={bulkCategoryValueId}
                    setBulkCategoryValueId={setBulkCategoryValueId}
                    selectedBulkCategory={selectedBulkCategory}
                    sortedCategoryValueOptions={sortedCategoryValueOptions}
                    bulkLoading={bulkLoading}
                    bulkMatchCount={bulkMatchCount}
                    bulkResults={bulkResults}
                    employees={employees}
                    handleBulkCategoryPreview={handleBulkCategoryPreview}
                    handleBulkCategoryConfirm={handleBulkCategoryConfirm}
                  />
                </UserSearchProvider>
              )}

              {wizardStep === 3 && (
                <ReportAccessSection
                  formData={{
                    collaborators: reportAccessUsers,
                  }}
                  onUpdate={setReportAccessUsers}
                />
              )}
            </PanelSection>
          </StepContent>

          {/* Navigation - updated for 3-step wizard */}
          <StepNavigationSection>
            <LeftButtons>
              <SaveDraftButton
                onClick={handleSaveDraft}
                disabled={
                  loading || !emailTpl.email_subject || !emailTpl.email_content
                }
              >
                {loading ? "Saving..." : "Save Draft"}
              </SaveDraftButton>
            </LeftButtons>

            <RightButtons>
              {wizardStep === 1 && (
                <NextButton
                  onClick={goToAssign}
                  disabled={!emailTpl.email_subject || !emailTpl.email_content}
                >
                  Next
                </NextButton>
              )}

              {wizardStep === 2 && (
                <>
                  <BackButton onClick={goBackToEmail}>Back</BackButton>
                  <NextButton
                    onClick={goToReportAccess}
                    disabled={assignedUsersList.length === 0}
                  >
                    Next
                  </NextButton>
                </>
              )}

              {wizardStep === 3 && (
                <>
                  <BackButton onClick={goBackToAssign}>Back</BackButton>
                  <SendButton
                    onClick={sendNow}
                    disabled={assignedUsersList.length === 0 || loading}
                  >
                    {loading ? "Sending..." : "Send"}
                  </SendButton>
                </>
              )}
            </RightButtons>
          </StepNavigationSection>
        </StepFormLayout>
      </FormContainer>
    </Container>
  );
};

// Employee Selection Styled Components
const EmployeeSelectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  background: #f0f0f0;
  padding: 3px;
  border-radius: 6px;
  margin-bottom: 16px;
  width: fit-content;
`;

const Tab = styled.button`
  padding: 8px 16px;
  background: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  border-radius: 4px;
  color: ${props => props.active ? '#414141' : '#7e7e7e'};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: ${props => props.active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'};
  white-space: nowrap;

  &:hover {
    background: ${props => props.active ? 'white' : '#e9ecef'};
  }
`;

const EmployeeSelectionLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-top: 16px;
  min-height: 500px;
`;

const SelectionPanel = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  overflow: hidden;
`;

const SelectedPanel = styled.div`
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const SelectedPanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0e0;
  background: white;
`;

const SummaryTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 13px;
  color: #414141;
`;

const ClearButton = styled.button`
  padding: 4px 8px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #ff4444;
    color: #ff4444;
  }
`;

const SelectionContent = styled.div`
  min-height: 300px;
`;

const SearchContainer = styled.div`
  margin-bottom: 16px;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  margin-bottom: 6px;
`;

const SearchIconWrapper = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #7e7e7e;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 8px 8px 36px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-size: 12px;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #476dfa;
  }
`;

const SearchHint = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  padding-left: 12px;
`;

const EmployeesList = styled.div`
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
`;

const EmployeeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #f8f8f8;

  &:hover {
    background: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }

  ${props => props.selected && `
    background: #f0f4ff;
    border-color: #476dfa;
  `}
`;

const EmployeeCheckbox = styled.input`
  width: 14px;
  height: 14px;
`;

const EmployeeDetails = styled.div`
  flex: 1;
`;

const EmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 12px;
  color: #414141;
  margin-bottom: 2px;
`;

const EmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
`;

const SearchPrompt = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SearchPromptIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
`;

const SearchPromptTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const SearchPromptText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
`;

const EmptyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const EmptyText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const SelectedEmployeesFullList = styled.div`
  flex: 1;
  padding: 16px;
  overflow-y: auto;
  max-height: 400px;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
`;

const SelectedEmployeeFullCard = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  margin-bottom: 8px;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
    box-shadow: 0 2px 4px rgba(71, 109, 250, 0.1);
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedEmployeeDetails = styled.div`
  flex: 1;
`;

const SelectedEmployeeName = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  color: #414141;
  margin-bottom: 2px;
`;

const SelectedEmployeeEmail = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 11px;
  color: #7e7e7e;
  margin-bottom: 2px;
`;

const SelectedEmployeeRole = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 10px;
  color: #999;
  background: #f0f0f0;
  padding: 2px 6px;
  border-radius: 3px;
  display: inline-block;
  margin-top: 4px;
`;

const RemoveButton = styled.button`
  width: 20px;
  height: 20px;
  padding: 0;
  background: #ff4444;
  border: none;
  border-radius: 50%;
  color: white;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #cc3333;
  }
`;

const EmptySelectedState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
`;

const EmptySelectedIcon = styled.div`
  font-size: 32px;
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptySelectedTitle = styled.div`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  margin-bottom: 6px;
`;

const EmptySelectedText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  max-width: 250px;
  line-height: 1.4;
`;

// Category Selection Styles
const CategoryHeaderRow = styled.div`
  margin-bottom: 12px;
`;

const CategoryHeaderTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  margin: 0 0 4px;
  font-family: "Poppins";
`;

const CategoryHeaderSubtitle = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0;
  font-family: "Poppins";
`;

const BulkCategoryBar = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: #f9fafb;
`;

const BulkCategorySelectors = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 8px;
`;

const BulkSelect = styled.div`
  flex: 1;
`;

const BulkSelectLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  font-family: "Poppins";
`;

const BulkNativeSelect = styled.select`
  width: 100%;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  font-size: 13px;
  background: #ffffff;
  font-family: "Poppins";
`;

const BulkCategoryActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const CategoryActionButton = styled.button`
  padding: 6px 10px;
  border-radius: 6px;
  border: none;
  background: #2d70e2;
  color: #ffffff;
  font-size: 13px;
  cursor: pointer;
  font-family: "Poppins";
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #1e5bc9;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const BulkMessage = styled.div`
  font-size: 12px;
  color: #374151;
  margin-top: 4px;
  font-family: "Poppins";
`;

const BulkMessageMuted = styled.div`
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;
  font-family: "Poppins";
`;

const BulkSampleList = styled.div`
  margin-top: 8px;
  border-top: 1px solid #e5e7eb;
  padding-top: 8px;
`;

const BulkSampleItem = styled.div`
  padding: 4px 0;
`;

const BulkSampleName = styled.div`
  font-size: 13px;
  font-weight: 500;
  font-family: "Poppins";
`;

const BulkSampleEmail = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-family: "Poppins";
`;

// Assign Recipients Content Component (wrapped in UserSearchProvider)
const AssignRecipientsContent = ({
  assignedUsersList,
  setAssignedUsersList,
  handleSelectItem,
  handleRemoveUser,
  clearAllUsers,
  activeTab,
  setActiveTab,
  filterCategories,
  bulkCategoryId,
  setBulkCategoryId,
  bulkCategoryValueId,
  setBulkCategoryValueId,
  selectedBulkCategory,
  sortedCategoryValueOptions,
  bulkLoading,
  bulkMatchCount,
  bulkResults,
  employees,
  handleBulkCategoryPreview,
  handleBulkCategoryConfirm,
}) => {
  const { userList, groupList } = useUserSearchContext();

  // Handle group selection - find users that match the group category
  const handleSelectItemWithGroups = (item) => {
    if (item.type === "group") {
      // Find users that belong to this group category
      const groupUsers = userList.filter((user) => {
        if (user?.userCategory && user?.userCategory?.length > 0) {
          return user?.userCategory?.some((cat) => cat.value_id === item.value);
        }
        return false;
      });

      if (groupUsers.length > 0) {
        const updatedUsers = [...assignedUsersList, ...groupUsers];
        const uniqueUsers = updatedUsers.filter((user, index, self) =>
          index === self.findIndex((t) => t.value === user.value)
        );
        setAssignedUsersList(uniqueUsers);
      }
    } else {
      handleSelectItem(item);
    }
  };

  return (
    <EmployeeSelectionContainer>
      {/* Tab Navigation */}
      <TabContainer>
        <Tab
          type="button"
          active={activeTab === "search"}
          onClick={() => setActiveTab("search")}
        >
          Search & Add
        </Tab>
        <Tab
          type="button"
          active={activeTab === "category"}
          onClick={() => setActiveTab("category")}
        >
          By Category
        </Tab>
      </TabContainer>

      <EmployeeSelectionLayout>
        {/* Left: Selection Interface */}
        <SelectionPanel>
          {activeTab === "search" && (
            <SelectionContent>
              <SearchContainer>
                <SearchDropdown
                  data={userList}
                  groupData={groupList}
                  onSearch={(item, searchTerm) =>
                    item.text.toLowerCase().includes(searchTerm.toLowerCase())
                  }
                  placeholder="Search User or Group"
                  onSelect={handleSelectItemWithGroups}
                />
                <SearchHint>
                  {userList?.length > 0
                    ? `${userList.length} contacts available. Search by name or select a group to add all members.`
                    : "Loading contacts..."}
                </SearchHint>
              </SearchContainer>
            </SelectionContent>
          )}

          {activeTab === "category" && (
            <SelectionContent>
              <CategoryHeaderRow>
                <CategoryHeaderTitle>Filter by Tags</CategoryHeaderTitle>
                <CategoryHeaderSubtitle>
                  Choose a category and value to find matching employees and add them to this survey.
                </CategoryHeaderSubtitle>
              </CategoryHeaderRow>
              {filterCategories.length === 0 ? (
                <BulkMessageMuted>
                  No tag categories are configured yet. Configure survey tags first, then you can filter by them here.
                </BulkMessageMuted>
              ) : (
                <BulkCategoryBar>
                  <BulkCategorySelectors>
                    <BulkSelect>
                      <BulkSelectLabel>Category</BulkSelectLabel>
                      <BulkNativeSelect
                        value={bulkCategoryId || ""}
                        onChange={(e) => {
                          const value = e.target.value || null;
                          setBulkCategoryId(value ? value : null);
                          setBulkCategoryValueId(null);
                        }}
                      >
                        <option value="">Select category…</option>
                        {filterCategories.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name}
                          </option>
                        ))}
                      </BulkNativeSelect>
                    </BulkSelect>
                    <BulkSelect>
                      <BulkSelectLabel>Value</BulkSelectLabel>
                      <Dropdown
                        selection
                        search
                        placeholder="Select value…"
                        value={bulkCategoryValueId || ""}
                        onChange={(e, { value }) =>
                          setBulkCategoryValueId(value || null)
                        }
                        disabled={!selectedBulkCategory}
                        options={sortedCategoryValueOptions}
                        fluid
                      />
                    </BulkSelect>
                  </BulkCategorySelectors>
                  <BulkCategoryActions>
                    <CategoryActionButton
                      type="button"
                      onClick={handleBulkCategoryPreview}
                      disabled={!bulkCategoryId || !bulkCategoryValueId || bulkLoading}
                    >
                      {bulkLoading ? "Checking..." : "Preview matches"}
                    </CategoryActionButton>
                    {bulkMatchCount > 0 && (
                      <CategoryActionButton
                        type="button"
                        onClick={handleBulkCategoryConfirm}
                        disabled={bulkLoading}
                      >
                        Add {bulkMatchCount} employee
                        {bulkMatchCount !== 1 ? "s" : ""}
                      </CategoryActionButton>
                    )}
                  </BulkCategoryActions>
                  {bulkMatchCount === 0 && !bulkLoading && (
                    <BulkMessageMuted>
                      Choose a category and value, then click "Preview matches" to see how many contacts will be added.
                    </BulkMessageMuted>
                  )}
                  {bulkMatchCount > 0 && (
                    <>
                      <BulkMessage>
                        Found {bulkMatchCount} matching contact
                        {bulkMatchCount !== 1 ? "s" : ""} that will be added.
                      </BulkMessage>
                      <BulkSampleList>
                        {bulkResults.slice(0, 10).map((row) => (
                          <BulkSampleItem
                            key={row.id || row.employee_id || row.employee}
                          >
                            <BulkSampleName>
                              {row.first_name} {row.last_name}
                            </BulkSampleName>
                            {row.email && (
                              <BulkSampleEmail>{row.email}</BulkSampleEmail>
                            )}
                          </BulkSampleItem>
                        ))}
                      </BulkSampleList>
                    </>
                  )}
                </BulkCategoryBar>
              )}
            </SelectionContent>
          )}
        </SelectionPanel>

        {/* Right: Selected Users */}
        <SelectedPanel>
          <SelectedPanelHeader>
            <SummaryTitle>
              Selected Recipients ({assignedUsersList.length})
            </SummaryTitle>
            {assignedUsersList.length > 0 && (
              <ClearButton onClick={clearAllUsers}>
                Clear All
              </ClearButton>
            )}
          </SelectedPanelHeader>

          {assignedUsersList.length > 0 ? (
            <SelectedEmployeesFullList>
              {assignedUsersList.map((user) => (
                <SelectedEmployeeFullCard key={user.value || user.id}>
                  <SelectedEmployeeDetails>
                    <SelectedEmployeeName>
                      {user.text || `${user.first_name || ''} ${user.last_name || ''}`.trim() || 'Unknown User'}
                    </SelectedEmployeeName>
                    {user.email && (
                      <SelectedEmployeeEmail>
                        {user.email}
                      </SelectedEmployeeEmail>
                    )}
                  </SelectedEmployeeDetails>
                  <RemoveButton
                    onClick={() => handleRemoveUser(user.value || user.id)}
                  >
                    ×
                  </RemoveButton>
                </SelectedEmployeeFullCard>
              ))}
            </SelectedEmployeesFullList>
          ) : (
            <EmptySelectedState>
              <EmptySelectedIcon>👥</EmptySelectedIcon>
              <EmptySelectedTitle>
                No recipients selected
              </EmptySelectedTitle>
              <EmptySelectedText>
                Use the search dropdown or category filter on the left to add survey recipients.
              </EmptySelectedText>
            </EmptySelectedState>
          )}
        </SelectedPanel>
      </EmployeeSelectionLayout>
    </EmployeeSelectionContainer>
  );
};

export default PulseScheduleSurvey;

// Styled Components - designed to look like a page instead of modal
const Container = styled.div`
  padding: 16px;
  max-width: 1400px;
  margin: 0 auto;
  animation: fadeIn 0.6s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const HeaderSection = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  color: #7e7e7e;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8f9fa;
    border-color: #476dfa;
    color: #476dfa;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 6px;
`;

const HeaderText = styled.div``;

const Title = styled.h1`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 24px;
  color: #414141;
  margin: 0 0 4px 0;
`;

const Subtitle = styled.p`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  margin: 0;
`;

const FormContainer = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const PanelSection = styled.div`
  margin-bottom: 0;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 16px;
  color: #414141;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;
`;

// Progress Indicator Components
const ProgressIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ProgressStep = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const ProgressStepNumber = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s;
  
  ${props => {
    if (props.completed) {
      return `
        background: #2e7d2e;
        color: white;
        border: 2px solid #2e7d2e;
      `;
    } else if (props.active) {
      return `
        background: #476dfa;
        color: white;
        border: 2px solid #476dfa;
      `;
    } else {
      return `
        background: #f0f0f0;
        color: #999;
        border: 2px solid #e0e0e0;
      `;
    }
  }}
`;

const ProgressStepText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 11px;
  color: #7e7e7e;
  text-align: center;
  white-space: nowrap;
`;

const ProgressConnector = styled.div`
  width: 40px;
  height: 2px;
  background: ${props => props.completed ? '#2e7d2e' : '#e0e0e0'};
  transition: background-color 0.2s;
`;

// Step Layout Components
const StepFormLayout = styled.div`
  min-height: 500px;
  display: flex;
  flex-direction: column;
`;

const StepContent = styled.div`
  flex: 1;
  padding: 24px;
`;

const StepNavigationSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
`;

const LeftButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CancelButton = styled.button`
  padding: 8px 18px;
  background: #f0f0f0;
  color: #7e7e7e;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.18s;

  &:hover {
    background: #e0e0e0;
  }
`;

const SaveDraftButton = styled.button`
  padding: 8px 18px;
  background: ${props => props.disabled ? '#cccccc' : '#28a745'};
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #218838;
  }
`;

const NextButton = styled.button`
  padding: 8px 18px;
  background: ${props => props.disabled ? '#cccccc' : '#476dfa'};
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

const SendButton = styled.button`
  padding: 8px 18px;
  background: ${props => props.disabled ? '#cccccc' : '#476dfa'};
  color: white;
  border: none;
  border-radius: 7px;
  font-size: 15px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;